import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import type { Scene } from '@lumen5/luminary';
import { AspectRatio, VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { MATCH_CUT } from '../video-state/serialization/transitionDescription';

import { resolveSceneHandle, sceneDisplayId } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import type { TransitionOption } from 'js/src/components/app/transitions-sidebar/transition-data';
import {
    getTransitionOptions,
    getTransitionUrlForOption,
    NONE_TRANSITION_URL,
    TRANSITION_GROUP_IDS,
    TRANSITION_LABELS,
    TRANSITION_URL_DATA,
} from 'js/src/components/app/transitions-sidebar/transition-data';
import { applyTransitionOperation } from 'js/src/components/app/transitions-sidebar/transition-operations';
import { checkpointVideo } from 'js/src/data/video-data';
import Flags from 'js/src/libs/flags';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import {
    saveRawLuminary,
    setVideoTransitionsEnabledOperation,
    syncMatchCutsWithTransitionsEnabledOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    applyMatchCutOperation,
    toggleSceneMorphEnabledOperation,
} from 'js/src/luminary/luminary-operations';
import {
    hasAppliedMatchCutWithNextScene,
    hasAppliedTransition,
} from 'js/src/luminary/luminary-selectors';
import type { MorphDisqualifiedReason } from 'js/src/luminary/morph-recommendation';

export { MATCH_CUT };

const NO_TRANSITION = 'none';

// Every group ships all four aspect ratios, so any one of them lists the group's variants.
const VARIANTS_BY_LABEL: ReadonlyMap<string, TransitionOption[]> = new Map(
    TRANSITION_GROUP_IDS.map((groupId) => [
        TRANSITION_LABELS[groupId],
        getTransitionOptions(groupId, AspectRatio.LANDSCAPE).map(({ option }) => option),
    ]),
);
const GROUP_ID_BY_LABEL: ReadonlyMap<string, string> = new Map(
    TRANSITION_GROUP_IDS.map((groupId) => [TRANSITION_LABELS[groupId], groupId]),
);
const TRANSITION_CHOICES = [NO_TRANSITION, MATCH_CUT, ...VARIANTS_BY_LABEL.keys()] as [
    string,
    ...string[],
];
const VARIANT_CHOICES = [...new Set([...VARIANTS_BY_LABEL.values()].flat())] as [
    TransitionOption,
    ...TransitionOption[],
];

const variantsText = (label: string): string => (VARIANTS_BY_LABEL.get(label) ?? []).join(', ');

/** Why a pair of scenes can't match cut, finishing "Scene A cannot match cut into scene B because…". */
const REFUSAL_BY_REASON: Record<MorphDisqualifiedReason, string> = {
    'no-morphable-assets':
        'one of them has nothing a match cut can carry across where they meet, though it may hold other content such as a chart or fancy captions',
    'incompatible-structure':
        'one of them has no text where they meet, and text is what a match cut carries across',
    'below-threshold':
        'their text does not line up: either the two scenes hold different numbers of text blocks, or the text sits in nearly the same place in both. A match cut reads best when matching text blocks sit in noticeably different positions',
    'shares-morphable-content':
        'they already share content that carries across on its own, such as the same visual element or identical text, so a match cut would have nothing to add',
    'consecutive-morph-limit':
        'the scene on the far side of one of them already match cuts, and a match cut cannot chain across three or more scenes',
    'adjacent-foreign-morph':
        'a neighbouring seam is already animated by another feature, such as an animated zoom, and a match cut cannot chain onto it',
    'scene-sequence':
        'one of them belongs to a pre-built scene sequence, which already animates between its scenes as it was designed to',
};

export const setSceneTransitionSchema = z.object({
    scene_id: z
        .string()
        .describe(
            'The scene_id, from the video state, of the scene the transition plays INTO — the second of the two scenes it joins.',
        ),
    transition: z
        .enum(TRANSITION_CHOICES)
        .describe(
            `How the scene is entered: "${MATCH_CUT}", a named transition, or "${NO_TRANSITION}" for a straight cut.`,
        ),
    variant: z
        .enum(VARIANT_CHOICES)
        .optional()
        .describe(
            `The transition's direction, shape or length; omit for its default. A ${MATCH_CUT.toLowerCase()} has none.`,
        ),
});
export type SetSceneTransitionArgs = z.infer<typeof setSceneTransitionSchema>;

function hasAuthoredGraphic(scene: Scene): boolean {
    const asset = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as HtmlAsset | undefined;
    return !!asset?.html;
}

export class SetSceneTransitionTool extends VideoStateTool<typeof setSceneTransitionSchema> {
    protected override readonly schema = setSceneTransitionSchema;
    protected override readonly toolName = TOOL_NAMES.setSceneTransition;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        `Sets how a scene is entered from the one before it: a ${MATCH_CUT.toLowerCase()}, a named transition, or a straight cut. A seam holds one of them, so setting one replaces whichever was there. ` +
        `A ${MATCH_CUT.toLowerCase()} slides the texts that pair up across the two scenes from where they sit in the first to where they sit in the second instead of cutting. Use it on a seam where the two scenes carry one connected idea, so the movement means something: a question and its answer, a problem and its fix, a before and an after, a claim and its evidence. It takes the seam over from the transition the scene design chose, so do not spend it on two scenes that merely sit next to each other, and leave it off a scene that has to look rigid. A scene pair it would not suit is refused, with a reason saying what to change. ` +
        'A named transition overlaps the two scenes by up to a second, which starts the scene it leads into that much earlier against its narration.';

    public override getToolDisplayName(): string {
        return 'Setting scene transition';
    }

    public get statusIcon(): string {
        return 'transition_fade';
    }

    public async execute(args: SetSceneTransitionArgs, context: ToolExecutionContext) {
        const { transition, variant } = args;
        const { parentActionGroup } = context;

        try {
            const { scene, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const prevScene = scene.prevScene;
            if (!prevScene) {
                return `Error: Scene ${sceneShortId} is the first scene in the video, so there is no scene before it to transition from. Pass the second of the two scenes you want to join.`;
            }
            const prevShortId = sceneDisplayId(this.luminary, prevScene.id);
            if (transition === MATCH_CUT && variant) {
                return `Error: ${MATCH_CUT} has no variants. Leave variant out.`;
            }

            // Seams another feature animates: nothing here changes them.
            if (prevScene.isPrePackagedMorphWithNextScene) {
                return transition === NO_TRANSITION
                    ? `Success: There is no transition into scene ${sceneShortId}. Scenes ${prevShortId} and ${sceneShortId} belong to a pre-built scene sequence, which animates between its scenes as it was designed to.`
                    : `Error: Scenes ${prevShortId} and ${sceneShortId} belong to a pre-built scene sequence, which already animates between its scenes as it was designed to.`;
            }
            const hadMatchCut = hasAppliedMatchCutWithNextScene(prevScene);
            if (prevScene.morphEnabled && !hadMatchCut) {
                return transition === NO_TRANSITION
                    ? `Success: There is no transition into scene ${sceneShortId}, but the seam from scene ${prevShortId} already animates through another feature, such as an animated zoom, which this tool does not change.`
                    : `Error: The seam between scenes ${prevShortId} and ${sceneShortId} already animates through another feature, such as an animated zoom, and a transition cannot share a seam with it.`;
            }

            // A match cut is kept on the scene before the seam, so adding or removing one changes
            // that scene too, and a subagent working alongside others may only do so on its own.
            const prevSceneGroupId = prevScene.sceneGroup?.id;
            const changesPrevScene = transition === MATCH_CUT || hadMatchCut;
            if (
                changesPrevScene &&
                context.subagentScope &&
                prevSceneGroupId &&
                !context.subagentScope.owns(prevSceneGroupId)
            ) {
                return `Error: The scene before ${sceneShortId} belongs to another subagent, so the seam between them isn't yours to change. Leave it, and say in your report if it should change.`;
            }

            const { applyMatchCut, toggleSceneMorphEnabled } = makeOperationsCallable(
                { applyMatchCutOperation, toggleSceneMorphEnabledOperation },
                this.luminary,
                this.dispatch,
                () => this.getState(),
            );
            if (transition === MATCH_CUT) {
                if (!this.luminary.template.transitionsEnabled) {
                    return 'Error: Scene transitions are turned off for this whole video, so a match cut set now would not survive a reload. Ask the user to switch "Scene transitions" back on in the Style tab first.';
                }
                if (hadMatchCut) {
                    return `Success: Scene ${sceneShortId} already opens with a match cut from scene ${prevShortId}.`;
                }
                const replacedTransition = hasAppliedTransition(scene);
                const recommendation = applyMatchCut(prevScene.id, false, parentActionGroup);
                if (!recommendation) {
                    throw new Error(`No scene pair to match cut into scene ${sceneShortId}`);
                }
                if (!recommendation.recommended) {
                    return `Error: Scene ${prevShortId} cannot match cut into scene ${sceneShortId} because ${REFUSAL_BY_REASON[recommendation.disqualifiedReason]}.`;
                }
                const replacedNote = replacedTransition
                    ? ', in place of the transition that was there'
                    : '';
                return `Success: Scene ${sceneShortId} now opens with a match cut from scene ${prevShortId}${replacedNote}.`;
            }

            if (transition === NO_TRANSITION && hadMatchCut) {
                toggleSceneMorphEnabled(prevScene.id, false, parentActionGroup);
                return `Success: Removed the match cut into scene ${sceneShortId}; it now cuts straight from scene ${prevShortId}.`;
            }

            const hadTransition = hasAppliedTransition(scene);
            const transitionsWereEnabled = this.luminary.template.transitionsEnabled;
            const parkedMatchCut = prevScene.matchCutTechnique !== VideoData.MATCH_CUT_NONE;

            let transitionUrl: string | null = NONE_TRANSITION_URL;
            if (transition !== NO_TRANSITION) {
                const variants = VARIANTS_BY_LABEL.get(transition) ?? [];
                if (variant && !variants.includes(variant)) {
                    return `Error: ${transition} has no "${variant}" variant. Its variants are ${variantsText(transition)}.`;
                }
                if (!transitionsWereEnabled && parkedMatchCut) {
                    return `Error: Scene transitions are turned off for this whole video, and switching them back on for this transition would also bring back the match cut saved from scene ${prevShortId} into scene ${sceneShortId}, which a seam cannot hold alongside it. Leave this seam as it is.`;
                }
                const groupId = GROUP_ID_BY_LABEL.get(transition) as string;
                transitionUrl = getTransitionUrlForOption(
                    groupId,
                    this.luminary.aspectRatio,
                    variant,
                );
                if (!transitionUrl) {
                    throw new Error(`No ${transition} transition for this aspect ratio`);
                }
                if (
                    transitionsWereEnabled &&
                    hadTransition &&
                    scene.customTransition.transitionUrl === transitionUrl
                ) {
                    return `Success: Scene ${sceneShortId} already opens with a ${transition} (${TRANSITION_URL_DATA[transitionUrl]?.optionId}) transition from scene ${prevShortId}.`;
                }
            } else if (!hadTransition) {
                const parkedNote =
                    parkedMatchCut && !transitionsWereEnabled
                        ? ' A match cut is still saved on this seam and comes back if the user switches "Scene transitions" on again in the Style tab.'
                        : '';
                return `Success: Scene ${sceneShortId} already cuts straight from scene ${prevShortId}.${parkedNote}`;
            }

            const {
                applyTransition,
                setVideoTransitionsEnabled,
                syncMatchCutsWithTransitionsEnabled,
            } = makeOperationsCallable(
                {
                    applyTransitionOperation,
                    setVideoTransitionsEnabledOperation,
                    syncMatchCutsWithTransitionsEnabledOperation,
                },
                this.luminary,
                this.dispatch,
                () => this.getState(),
            );
            if (hadMatchCut) {
                toggleSceneMorphEnabled(prevScene.id, false, parentActionGroup);
            }
            const enablesTransitions = !!transitionUrl && !transitionsWereEnabled;
            if (enablesTransitions) {
                setVideoTransitionsEnabled(true, false, parentActionGroup);
                syncMatchCutsWithTransitionsEnabled(true, false, parentActionGroup);
            }
            applyTransition(scene.id, transitionUrl);
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            const retimeNote =
                hasAuthoredGraphic(scene) && Flags.canUseCodeo()
                    ? ` Scene ${sceneShortId}'s graphic was timed to its narration before this change moved its start, so re-time it against its narration with ${TOOL_NAMES.editHtmlAsset}.`
                    : '';
            if (!transitionUrl) {
                return `Success: Removed the transition into scene ${sceneShortId}; it now cuts straight from scene ${prevShortId}.${retimeNote}`;
            }
            const appliedVariant = TRANSITION_URL_DATA[transitionUrl]?.optionId;
            const replacedNote = hadMatchCut ? ', in place of the match cut' : '';
            const enabledNote = enablesTransitions
                ? ' Scene transitions were off for the whole video, so this switched them back on, which also brings back any transitions and match cuts saved on other scenes.'
                : '';
            return `Success: Scene ${sceneShortId} now opens with a ${transition} (${appliedVariant}) transition from scene ${prevShortId}${replacedNote}.${enabledNote}${retimeNote}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting the scene transition: ${errorMessage}`;
        }
    }
}
